// Front-end service for the in-page AI "Try it Yourself" experience.
// All server logic lives in the NodeServer (Express) backend at staging.
// Demo-specific routes are under /ai-demo/* so they never touch the existing
// /assistants/* in-app chat routes.

const AI_API_BASE =
  (typeof process !== "undefined" && process.env && process.env.GATSBY_AI_API_BASE) ||
  "https://staging.visiomate.com"

const USER_DATA_KEY = "User Data"
const CHAT_LIMIT = 2
const CALL_LIMIT = 1

const isBrowser = () => typeof window !== "undefined"

// ── Email + lead ────────────────────────────────────────────────────────────

// Returns true when the address is deliverable (backend status === "valid").
export const verifyEmail = async email => {
  const res = await fetch(`${AI_API_BASE}/ai-demo/verify-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
    redirect: "follow",
  })
  const data = await res.json().catch(() => ({}))
  return data?.status === "valid"
}

// Creates the Odoo lead/user-access record. On success persists the returned
// "User Data" payload and returns true.
export const createHrLead = async ({ name, email }) => {
  const res = await fetch(`${AI_API_BASE}/create_hr_lead`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email }),
  })
  const data = await res.json().catch(() => ({}))
  if (data?.result?.status) {
    // Source stores the nested `.data` string for the lead response.
    setUserDataRaw(data?.result?.[USER_DATA_KEY]?.data)
    return true
  }
  return false
}

// Increments usage for an assistant (kind: "chat" | "call") in Odoo and
// persists the refreshed "User Data". Returns true on success.
export const updateUserUsage = async ({ email, name, kind }) => {
  const record = getUserData()
  const current = record[name] || {}
  const next = {
    ...record,
    [name]: { ...current, [kind]: current[kind] ? current[kind] + 1 : 1 },
  }
  const res = await fetch(`${AI_API_BASE}/update_user_usage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, data: JSON.stringify(next) }),
    redirect: "follow",
  })
  const data = await res.json().catch(() => ({}))
  if (data?.result?.status) {
    // Usage response stores the "User Data" value directly (not nested .data).
    setUserDataRaw(data?.result?.[USER_DATA_KEY])
    return true
  }
  return false
}

// ── OpenAI thread (chat) ──────────────────────────────────────────────────────

export const createThread = async () => {
  const res = await fetch(`${AI_API_BASE}/ai-demo/threads`, { method: "POST" })
  const data = await res.json()
  return data.threadId
}

// Returns the raw Response so the caller can stream response.body.
export const sendThreadMessage = (threadId, { content, path }) =>
  fetch(`${AI_API_BASE}/ai-demo/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ threadId, content, path }),
  })

export const deleteThread = async threadId => {
  if (!threadId) return false
  const res = await fetch(`${AI_API_BASE}/ai-demo/delete-thread`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ threadId }),
  })
  const data = await res.json().catch(() => ({}))
  return !!data.deleted
}

// ── localStorage: User Data + remaining tries ─────────────────────────────────

export const getUserData = () => {
  if (!isBrowser()) return {}
  const raw = localStorage.getItem(USER_DATA_KEY)
  if (!raw || raw === "undefined") return {}
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

export const setUserDataRaw = value => {
  if (!isBrowser() || value == null) return
  localStorage.setItem(USER_DATA_KEY, typeof value === "string" ? value : JSON.stringify(value))
}

// { chat, call } remaining for an assistant name.
export const getRemaining = name => {
  const rec = getUserData()[name] || {}
  return {
    chat: Math.max(0, CHAT_LIMIT - (rec.chat || 0)),
    call: Math.max(0, CALL_LIMIT - (rec.call || 0)),
  }
}

// ── Session cookie ────────────────────────────────────────────────────────────

export const getSessionEmail = () => {
  if (!isBrowser()) return null
  const match = document.cookie.match(/(?:^|;\s*)sessionEmail=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

export const setSessionEmail = email => {
  if (!isBrowser()) return
  document.cookie = `sessionEmail=${email}; path=/; max-age=3600`
}

export { CHAT_LIMIT, CALL_LIMIT }
